// The Overwolf subscription-cancellation page (/ow-cancel).
//
// Opened in the user's default browser from the Overwolf app, e.g.
//   /songhunt/ow-cancel?userId=1837&email=x%40y.ai&utm_source=overwolf&utm_campaign=ow_cancel
//
// Shares its constraints (and its visual world) with OwSurvey:
//
// - It renders OUTSIDE the App shell (a top-level route in app/router.tsx, sibling
//   of '/'), so there is no NavBar, no store bootstrap, no session, no toasts and
//   no modals. That is also why the affiliate-link popup can never appear here:
//   startAffiliationTimer() only ever runs from login/auto-login inside the shell.
// - Because AppLoader is not mounted, this route must remove the static
//   #app-preloader from index.html itself - otherwise the preloader covers the
//   page forever (see the react-basename-bare-path-preloader-hang note).
// - `userId` / `email` arrive as URL params and are unverified input (anyone can
//   edit a URL). They are displayed and echoed, never trusted or used to authorize
//   anything - the actual cancellation is handled by the team, off this page.
//
// Layout: ONE centered card, not the survey's split page. This is a decision with
// a single question in it, so everything the user needs sits in one column they
// read top to bottom - identity, what stops working, then the two ways out.
//
// The page's job is retention: state plainly what stops working, make keeping the
// subscription the easy path, and leave cancelling available but quiet.
//
// Cancelling calls no API - there is no endpoint. The request travels by e-mail to
// the team, so the last step tells the user exactly what to send and opens their
// mail client with it already written (see shared/support/account-request-email).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SonghuntLogo from '../../shared/components/SonghuntLogo';
import { assetUrl } from '../../shared/asset-url';
import { logger } from '../../shared/logging/logger';
import AccountRequestEmailStep from '../../shared/support/AccountRequestEmail';
import { buildUnsubscribeEmail } from '../../shared/support/account-request-email';
import './OwCancel.scss';

type View = 'idle' | 'kept' | 'requested';

/** Seconds the "nothing has changed" confirmation shows before closing the tab. */
const KEEP_CLOSE_SECONDS = 3;

// What the user stops getting. Mirrors the Streamer bullets on the Overwolf signup
// landing (LoginRegister.tsx HUNTER_FEATURES_GAMING) as plain strings - this page
// must not import anything from the app shell. Deliberately does NOT name the
// plan: the link carries no plan information, so claiming one could be wrong.
const LOSES: string[] = [
  'Unlimited Adaptive Music',
  "'Stream Mode' for 100% licensed music suitable for streaming on YouTube, Twitch, Social, etc.",
  'Unlimited Rematches',
  'Unlimited Prompt and Filter Song Searches',
  'Hunter Songhunt.ai access',
];

/** An empty or malformed value is treated the same as absent rather than shown as junk. */
function readEmail(params: URLSearchParams): string | undefined {
  const raw = params.get('email')?.trim();
  if (!raw || !raw.includes('@')) return undefined;
  return raw;
}

/** The check mark shared by both confirmation states. */
function CheckIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export default function OwCancel() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const email = useMemo(() => readEmail(params), [params]);
  // The Overwolf link sends `userId`; `userID` is accepted too because the
  // uninstall survey link spells it that way and the two are easy to mix up.
  const userId = useMemo(
    () => (params.get('userId') ?? params.get('userID'))?.trim() || undefined,
    [params],
  );

  const [view, setView] = useState<View>('idle');
  const [secondsLeft, setSecondsLeft] = useState(KEEP_CLOSE_SECONDS);
  // window.close() is a no-op for a tab the script did not open, which is exactly
  // this tab (Overwolf hands the URL to the OS). We try anyway - it costs nothing
  // and works when the page WAS script-opened - and fall back to telling the user.
  const [closeBlocked, setCloseBlocked] = useState(false);

  // This route is outside the App shell, so AppLoader never runs - take the
  // preloader down here or it hides the page permanently.
  useEffect(() => {
    document.getElementById('app-preloader')?.remove();
    document.title = 'Songhunt - Cancel subscription';
  }, []);

  const keptTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const keepSubscription = useCallback(() => {
    logger.info('app', 'ow-cancel kept subscription', { userId, hasEmail: !!email });
    setView('kept');
  }, [email, userId]);

  const requestCancellation = useCallback(() => {
    // TODO: post the cancellation request to the backend once the endpoint exists,
    // and drop the e-mail step below with it.
    logger.info('app', 'ow-cancel cancellation requested', { userId, hasEmail: !!email });
    setView('requested');
  }, [email, userId]);

  // Countdown + auto-close for the "nothing has changed" state.
  useEffect(() => {
    if (view !== 'kept') return;

    // Floors at 1: the countdown reads 3, 2, 1 and is then replaced by the close
    // (or by the "close it yourself" fallback). "Closing this tab in 0..." would
    // sit on screen for the moment in between.
    const tick = setInterval(() => {
      setSecondsLeft(prev => (prev > 1 ? prev - 1 : 1));
    }, 1000);

    const close = setTimeout(() => {
      window.close();
      // If the tab is still here a moment later the browser refused, as it does
      // for tabs it did not open via script. Say so instead of leaving a dead
      // countdown on screen.
      const check = setTimeout(() => setCloseBlocked(true), 400);
      keptTimers.current.push(check);
    }, KEEP_CLOSE_SECONDS * 1000);

    keptTimers.current.push(close);
    const timers = keptTimers.current;

    return () => {
      clearInterval(tick);
      timers.forEach(clearTimeout);
      timers.length = 0;
    };
  }, [view]);

  return (
    <div className="ow-cancel">
      <main className="ow-cancel__card">
        <div className="ow-cancel__lockup">
          <SonghuntLogo className="ow-cancel__sh-logo" />
          <span className="ow-cancel__plus" aria-hidden="true">
            +
          </span>
          <img
            className="ow-cancel__ow-logo"
            src={assetUrl('assets/logos/overwolf-logo.svg')}
            alt="Overwolf"
          />
        </div>

        {view === 'idle' && (
          <>
            <div className="ow-cancel__head">
              <p className="ow-cancel__eyebrow">Subscription</p>
              <h1 className="ow-cancel__title">You are about to cancel.</h1>
              <p className="ow-cancel__sub">
                Your music stops adapting to your gameplay the moment this goes through. Everything
                you have built up - your playlists, your matches, your stream-safe library - goes
                with it.
              </p>

              {email ? (
                <span className="ow-cancel__who">
                  <span className="ow-cancel__avatar" aria-hidden="true">
                    {email[0]}
                  </span>
                  Signed in as <b>{email}</b>
                </span>
              ) : (
                <span className="ow-cancel__anon">
                  We could not read your account from this link - our team will confirm who you are
                  before anything changes.
                </span>
              )}
            </div>

            <div className="ow-cancel__rule" aria-hidden="true" />

            <h2 className="ow-cancel__question">Here is what you lose today:</h2>

            <ul className="ow-cancel__loses">
              {LOSES.map(item => (
                <li className="ow-cancel__lose" key={item}>
                  <span className="ow-cancel__x" aria-hidden="true">
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                    >
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </span>
                  <span className="ow-cancel__lose-text">{item}</span>
                </li>
              ))}
            </ul>

            <div className="ow-cancel__actions">
              <button className="ow-cancel__keep" type="button" onClick={keepSubscription}>
                Keep my subscription
              </button>
              <button className="ow-cancel__cancel" type="button" onClick={requestCancellation}>
                Cancel my subscription
              </button>
            </div>
          </>
        )}

        {view === 'kept' && (
          <div className="ow-cancel__result" role="status">
            <span className="ow-cancel__badge ow-cancel__badge--keep" aria-hidden="true">
              <CheckIcon />
            </span>
            <h1 className="ow-cancel__result-title">Nothing has changed.</h1>
            <p className="ow-cancel__sub">
              Your subscription is still active and your music keeps adapting. Go back to your game
              - we will not bring this up again.
            </p>
            <p className="ow-cancel__countdown">
              {closeBlocked ? 'You can close this tab now.' : `Closing this tab in ${secondsLeft}...`}
            </p>
          </div>
        )}

        {view === 'requested' && (
          <div className="ow-cancel__panel-inner ow-cancel__result" role="status">
            <span className="ow-cancel__badge" aria-hidden="true">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </span>
            <h2 className="ow-cancel__result-title">One last step to cancel.</h2>
            <p className="ow-cancel__sub">
              Cancellations are handled by a person, so we need it in writing. Send us an e-mail with
              the subject below and we will close your subscription and confirm
              {email ? (
                <>
                  {' '}
                  at <b>{email}</b>
                </>
              ) : null}
              . The button fills the whole message in for you.
            </p>

            {email && (
              <AccountRequestEmailStep
                block="ow-cancel"
                request={buildUnsubscribeEmail({ email, userId })}
                action="Open my email app"
              />
            )}

            <p className="ow-cancel__countdown">
              Until we receive it, nothing changes - your subscription stays active.
            </p>
          </div>
        )}

      </main>
    </div>
  );
}
